import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { defaultLandingPath } from '../utils/divisions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function RegisterInvite() {
  const { token } = useParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const { registerViaInvite } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Decode the token to get email and name for display
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setEmail(payload.email || '');
      setName(payload.name || payload.email?.split('@')[0] || '');
      
      // Check if token is expired
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        setTokenError('This invite link has expired. Please request a new invite from your manager.');
      }
    } catch (e) {
      setTokenError('Invalid invite link. Please check the URL or request a new invite.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const userData = await registerViaInvite(token, password);
      toast.success('Account created successfully!');
      navigate(defaultLandingPath(userData), { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Invalid Invite</h2>
          <p className="text-slate-600 mb-6">{tokenError}</p>
          <Button onClick={() => navigate('/login')} className="bg-slate-900 hover:bg-slate-800">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm p-8">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-black text-slate-900 mb-2">Complete Registration</h2>
          <p className="text-slate-600">Set your password to activate your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-invite-form">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              disabled
              className="h-12 bg-slate-50"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Your Name</Label>
            <Input
              id="name"
              type="text"
              value={name}
              disabled
              className="h-12 bg-slate-50"
              data-testid="name-display"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="At least 6 characters"
              data-testid="password-input"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Re-enter your password"
              data-testid="confirm-password-input"
              className="h-12"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-button"
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
      </div>
    </div>
  );
}
